"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.DialTracker = void 0;
var _react = _interopRequireWildcard(require("react"));
var _reactMintl = require("@godaddy/react-mintl");
var _intents = _interopRequireDefault(require("@ux/intents"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _ComponentDataProvider = require("../../../ComponentDataProvider");
var _DialTracker = require("../../../styles/DialTracker");
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
const intents = new _intents.default();

/**
 * Ease-out cubic function for smooth deceleration
 * Starts fast and ends slow
 */
const easeOutCubic = t => {
  return 1 - Math.pow(1 - t, 3);
};

/**
 * Generates discrete pill-shaped segments for the semi-circle dial
 */
function generateDialSegments({
  totalSegments,
  filledSegments,
  size,
  strokeWidth,
  gapInPixels,
  totalAngle
}) {
  const radius = (size - strokeWidth) / 2;
  const centerX = size / 2;
  const centerY = size / 2;

  // Calculate angles for each segment
  const gapAngle = gapInPixels / radius; // Convert pixel gap to radians
  const segmentAngle = (totalAngle - (totalSegments - 1) * gapAngle) / totalSegments;

  // Generate segments
  const segments = [];
  for (let i = 0; i < totalSegments; i++) {
    const startAngle = Math.PI + i * (segmentAngle + gapAngle); // Start from left (180°)
    const endAngle = startAngle + segmentAngle;

    // Convert to coordinates
    const x1 = centerX + radius * Math.cos(startAngle);
    const y1 = centerY + radius * Math.sin(startAngle);
    const x2 = centerX + radius * Math.cos(endAngle);
    const y2 = centerY + radius * Math.sin(endAngle);

    // Create path for this segment
    const path = `M ${x1} ${y1} A ${radius} ${radius} 0 0 1 ${x2} ${y2}`;
    const isFilled = i < filledSegments;
    segments.push({
      path,
      isFilled,
      x1,
      y1,
      x2,
      y2
    });
  }
  return segments;
}

/**
 * Renders dial segment paths
 */
const DialSegmentPaths = ({
  segments,
  innerStrokeColor,
  outerStrokeColor,
  strokeWidth
}) => /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, segments.map((segment, index) => /*#__PURE__*/_react.default.createElement("path", {
  key: index,
  d: segment.path,
  fill: "none",
  stroke: segment.isFilled ? innerStrokeColor : outerStrokeColor,
  strokeWidth: strokeWidth,
  strokeLinecap: "butt"
})));

/**
 * Renders end caps for the dial
 */
const DialEndCaps = ({
  segments,
  innerStrokeColor,
  outerStrokeColor,
  strokeWidth
}) => {
  if (segments.length === 0) return null;
  return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement("circle", {
    cx: segments[0].x1,
    cy: segments[0].y1,
    r: strokeWidth / 2,
    fill: segments[0].isFilled ? innerStrokeColor : outerStrokeColor
  }), /*#__PURE__*/_react.default.createElement("circle", {
    cx: segments[segments.length - 1].x2,
    cy: segments[segments.length - 1].y2,
    r: strokeWidth / 2,
    fill: segments[segments.length - 1].isFilled ? innerStrokeColor : outerStrokeColor
  }));
};

/**
 * DialTracker Component
 *
 * Displays a rating as a semi-circular dial with discrete segments.
 *
 * @example
 * // Static display
 * <DialTracker label="Rating" rating={3} description="Quality" maxRating={5} />
 *
 * @example
 * // With animation (segments fill progressively with ease-out easing)
 * <DialTracker
 *   label="Rating"
 *   rating={3}
 *   description="Quality"
 *   maxRating={5}
 *   animated={true}
 *   animationDuration={1500}
 * />
 */
const DialTracker = props => {
  const {
    overrideProps
  } = (0, _ComponentDataProvider.useComponentDataProvider)({});
  const {
    label,
    rating,
    description,
    maxRating = 5,
    outerStrokeColor = intents.ux.boxLowContrast.borderColor,
    innerStrokeColor = '#1BDBDB',
    labelPosition = 'top',
    animated = false,
    animationDuration = 1500,
    size = 200,
    valueFontSize = 44,
    labelFontSize = 24,
    descriptionFontSize = 14
  } = {
    ...props,
    ...overrideProps
  };
  const finalRating = Math.min(rating, maxRating);

  // Configurable constants for dial geometry
  const DIAL_SIZE = size; // Total SVG width/height
  const STROKE_WIDTH = size / 20; // Width of each segment stroke (proportional to size)
  const TOTAL_SEGMENTS = 5; // Number of discrete segments
  const GAP_BETWEEN_SEGMENTS = size / 50; // Gap between segments in pixels (proportional to size)
  const TOTAL_ANGLE = Math.PI; // Total angle of the dial (Math.PI = 180° semi-circle)

  // Calculate how many segments to fill (target value)
  const targetFilledSegments = Math.round(finalRating / maxRating * TOTAL_SEGMENTS);

  // State for animated progress
  const [animatedProgress, setAnimatedProgress] = (0, _react.useState)(animated ? 0 : targetFilledSegments);

  // Animation effect
  (0, _react.useEffect)(() => {
    if (!animated) {
      setAnimatedProgress(targetFilledSegments);
      return;
    }

    // Start animation from 0
    setAnimatedProgress(0);
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / animationDuration, 1);

      // Apply ease-out cubic easing
      const easedProgress = easeOutCubic(progress);
      const currentValue = easedProgress * targetFilledSegments;
      setAnimatedProgress(currentValue);
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [animated, targetFilledSegments, animationDuration]);

  // Use animated or static value for filled segments
  const filledSegments = Math.round(animatedProgress);

  // Generate the dial segments (memoized for performance)
  const segments = (0, _react.useMemo)(() => generateDialSegments({
    totalSegments: TOTAL_SEGMENTS,
    filledSegments,
    size: DIAL_SIZE,
    strokeWidth: STROKE_WIDTH,
    gapInPixels: GAP_BETWEEN_SEGMENTS,
    totalAngle: TOTAL_ANGLE
  }), [TOTAL_SEGMENTS, filledSegments, DIAL_SIZE, STROKE_WIDTH, GAP_BETWEEN_SEGMENTS, TOTAL_ANGLE]);
  const flexDir = labelPosition === 'top' ? 'column-reverse' : 'column';
  const containerStyle = {
    flexDirection: flexDir
  };
  const meterStyle = {
    position: 'relative',
    width: DIAL_SIZE
  };
  const svgStyle = {
    overflow: 'visible'
  };
  const labelMargin = labelPosition === 'top' ? '0 0 16px 0' : '16px 0 0 0';

  // Calculate fixed height for description based on font size (3 lines with line-height 1.4)
  const descriptionHeight = descriptionFontSize * 1.4 * 3;
  const descriptionStyle = {
    fontSize: `${descriptionFontSize}px`,
    height: `${descriptionHeight}px`
  };
  return /*#__PURE__*/_react.default.createElement(_DialTracker.DialTrackerContainer, {
    "data-cy": "dial-tracker-container",
    style: containerStyle
  }, /*#__PURE__*/_react.default.createElement(_DialTracker.DialMeter, {
    "data-cy": "dial-meter",
    style: meterStyle
  }, /*#__PURE__*/_react.default.createElement("svg", {
    width: DIAL_SIZE,
    height: DIAL_SIZE / 2 + 20,
    style: svgStyle
  }, /*#__PURE__*/_react.default.createElement(DialSegmentPaths, {
    segments: segments,
    innerStrokeColor: innerStrokeColor,
    outerStrokeColor: outerStrokeColor,
    strokeWidth: STROKE_WIDTH
  }), /*#__PURE__*/_react.default.createElement(DialEndCaps, {
    segments: segments,
    innerStrokeColor: innerStrokeColor,
    outerStrokeColor: outerStrokeColor,
    strokeWidth: STROKE_WIDTH
  })), /*#__PURE__*/_react.default.createElement(_DialTracker.DialValue, {
    "data-cy": "dial-value",
    style: {
      fontSize: `${valueFontSize}px`
    }
  }, finalRating, "/", maxRating)), /*#__PURE__*/_react.default.createElement(_DialTracker.DialLabelAndDescription, {
    "data-cy": "dial-label-and-description",
    style: {
      margin: labelMargin
    }
  }, /*#__PURE__*/_react.default.createElement(_DialTracker.DialLabel, {
    "data-cy": "dial-label",
    style: {
      fontSize: `${labelFontSize}px`
    }
  }, /*#__PURE__*/_react.default.createElement(_reactMintl.FormattedMessage, {
    id: label
  })), /*#__PURE__*/_react.default.createElement(_DialTracker.DialDescription, {
    "data-cy": "dial-description",
    style: descriptionStyle
  }, /*#__PURE__*/_react.default.createElement(_reactMintl.FormattedMessage, {
    id: description
  }))));
};
exports.DialTracker = DialTracker;
const DialTrackerWithComponentDataProvider = props => {
  const {
    appName,
    apiKey,
    plid,
    urlOverride,
    uipProps,
    forceEnableBFF
  } = props;
  return /*#__PURE__*/_react.default.createElement(_ComponentDataProvider.ComponentDataProvider, {
    appName: appName,
    componentName: "shared-components",
    apiKey: apiKey,
    forceEnableBFF: forceEnableBFF,
    uipProps: {
      plid,
      urlOverride,
      ...uipProps
    }
  }, /*#__PURE__*/_react.default.createElement(DialTracker, props));
};

// Add PropTypes for the wrapper component
DialTrackerWithComponentDataProvider.propTypes = {
  // Include all DialTracker props (would need to be defined properly in a real implementation)
  appName: _propTypes.default.string.isRequired,
  apiKey: _propTypes.default.string.isRequired,
  plid: _propTypes.default.number,
  urlOverride: _propTypes.default.string,
  uipProps: _propTypes.default.object,
  forceEnableBFF: _propTypes.default.bool
};
var _default = exports.default = DialTrackerWithComponentDataProvider;